import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { AccountingDocType } from '../../../../api/types/accounting/accounting-documents-entity';
import { CardBrandType } from '../../../../api/types/students/credit-card-tokens/credit-card-token-entity';
import { PaymentMethodsDb } from '../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import globalPaymentServices from '../../../../stores/global-payment-services';
import globalSettingsBranches from '../../../../stores/global-settings-branches';
import {
  AccountingDocumentsFilterType,
  defaultAccountingDocumentsFilter,
} from '../accounting-documents-filter.type';
import { SelectPaymentServicesFilterDialog } from './select-payment-services-filter-dialog';

interface AccountingDocumentsFilterProps {
  filters: AccountingDocumentsFilterType;
  loading?: boolean;
  onChange: (filters: AccountingDocumentsFilterType) => void;
  onSearch: () => void;
  onClear?: () => void;
}

const docTypeOptions = [
  { label: 'קבלה', value: AccountingDocType.RECEIPT },
  /*{ label: 'חשבונית', value: AccountingDocType.INVOICE },*/
  { label: 'קבלה לתרומה', value: AccountingDocType.DONATION_RECEIPT },
  /*{ label: 'שובר משיכה מקופה', value: AccountingDocType.CASH_WITHDRAWAL },*/
  { label: 'קבלת - החזר', value: AccountingDocType.RECEIPT_REFUND },
  { label: 'חשבון', value: AccountingDocType.BILL },
];

const paymentMethodOptions = [
  { label: 'כרטיס אשראי', value: PaymentMethodsDb.CreditCard },
  { label: 'מזומן', value: PaymentMethodsDb.Cash },
  { label: 'הנחה', value: PaymentMethodsDb.Discount },
  { label: 'העברה בנקאית', value: PaymentMethodsDb.BankTransfer },
  { label: 'מלגה', value: PaymentMethodsDb.Scholarship },
  { label: 'המחאה', value: PaymentMethodsDb.Check },
];

const cardBrandOptions = [
  { label: 'Visa', value: CardBrandType.VISA },
  { label: 'Mastercard', value: CardBrandType.MASTERCARD },
  { label: 'Isracard', value: CardBrandType.ISRACARD },
  { label: 'American Express', value: CardBrandType.AMERICAN_EXPRESS },
  { label: 'Diners Club', value: CardBrandType.DINERS_CLUB },
  { label: 'Discover', value: CardBrandType.DISCOVER },
  { label: 'JCB', value: CardBrandType.JCB },
  { label: 'Maestro', value: CardBrandType.MAESTRO },
  { label: 'כרטיס פרטי', value: CardBrandType.PRIVATE_LABEL },
];

export const AccountingDocumentsFilter: React.FC<AccountingDocumentsFilterProps> =
  observer(({ filters, loading, onChange, onSearch, onClear }) => {
    const [showPaymentServicesDialog, setShowPaymentServicesDialog] =
      useState(false);

    const update = <K extends keyof AccountingDocumentsFilterType>(
      key: K,
      value: AccountingDocumentsFilterType[K],
    ) => {
      onChange({ ...filters, [key]: value });
    };

    const handleClear = () => {
      if (onClear) {
        onClear();
        return;
      }
      onChange(defaultAccountingDocumentsFilter());
    };

    const selectedPaymentServiceCount = filters.paymentServiceIds.length;

    const paymentServicesTooltip = (() => {
      if (selectedPaymentServiceCount === 0) {
        return 'לא נבחרו שירותי תשלום';
      }
      const idSet = new Set(filters.paymentServiceIds);
      // Read from MobX store so the observer re-renders when services hydrate.
      const names = globalPaymentServices.services
        .filter((s) => idSet.has(s.id))
        .map((s) => s.paymentServiceName);
      if (names.length === 0) {
        return filters.paymentServiceIds.join(', ');
      }
      return names.join(', ');
    })();

    const branchOptions = globalSettingsBranches.branches.map((b) => ({
      label: b.branchName,
      value: b.id,
    }));

    const styles = {
      container: {
        background: 'white',
        borderRadius: '8px',
        padding: '16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '12px',
      },
      header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      title: {
        fontSize: '1.1rem',
        fontWeight: 600 as const,
        color: '#333',
      },
      grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
        gap: '12px',
      },
      field: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: '4px',
      },
      label: {
        fontSize: '0.8rem',
        color: '#555',
      },
      rangeRow: {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
      },
      inputFull: {
        width: '100%',
      },
      footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap' as const,
        gap: '12px',
        borderTop: '1px solid #eee',
        paddingTop: '12px',
      },
      checkboxRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
      },
      checkboxItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      },
      buttonsRow: {
        display: 'flex',
        gap: '8px',
      },
    };

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <div style={styles.title}>סינונים</div>
        </div>

        <div style={styles.grid}>
          <div style={styles.field}>
            <label style={styles.label}>שלוחות</label>
            <MultiSelect
              value={filters.branchIds}
              options={branchOptions}
              onChange={(e) => update('branchIds', e.value ?? [])}
              placeholder="הכל"
              display="chip"
              filter
              maxSelectedLabels={2}
              selectedItemsLabel={`${filters.branchIds.length} שלוחות נבחרו`}
              style={styles.inputFull}
              disabled={loading}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>סוג מסמך</label>
            <Dropdown
              value={filters.accountingType}
              options={docTypeOptions}
              onChange={(e) => update('accountingType', e.value ?? null)}
              placeholder="הכל"
              showClear
              style={styles.inputFull}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>אמצעי תשלום</label>
            <Dropdown
              value={filters.paymentMethod}
              options={paymentMethodOptions}
              onChange={(e) => update('paymentMethod', e.value ?? null)}
              placeholder="הכל"
              showClear
              style={styles.inputFull}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>שירותי תשלום</label>
            <Button
              type="button"
              label={
                selectedPaymentServiceCount === 0
                  ? 'הכל'
                  : String(selectedPaymentServiceCount)
              }
              icon="pi pi-list"
              outlined
              severity="secondary"
              onClick={() => setShowPaymentServicesDialog(true)}
              disabled={loading}
              tooltip={paymentServicesTooltip}
              tooltipOptions={{ position: 'top', showOnDisabled: true }}
              style={{
                ...styles.inputFull,
                justifyContent: 'space-between',
                direction: 'rtl',
              }}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>חברת אשראי</label>
            <MultiSelect
              value={filters.creditCardCompany}
              options={cardBrandOptions}
              onChange={(e) => update('creditCardCompany', e.value ?? [])}
              placeholder="הכל"
              display="chip"
              style={styles.inputFull}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>4 ספרות אחרונות</label>
            <InputText
              value={filters.creditCardLast4Digit ?? ''}
              onChange={(e) => {
                const digitsOnly = e.target.value
                  .replace(/\D/g, '')
                  .slice(0, 4);
                update(
                  'creditCardLast4Digit',
                  digitsOnly === '' ? null : digitsOnly,
                );
              }}
              keyfilter="int"
              maxLength={4}
              placeholder="0000"
              style={styles.inputFull}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>תיאור פריט מכיל</label>
            <InputText
              value={filters.itemDescriptionSubText ?? ''}
              onChange={(e) =>
                update(
                  'itemDescriptionSubText',
                  e.target.value === '' ? null : e.target.value,
                )
              }
              maxLength={200}
              placeholder="חיפוש בתיאור..."
              style={styles.inputFull}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>מספר סידורי</label>
            <InputNumber
              value={filters.serialNumber}
              onValueChange={(e) => update('serialNumber', e.value ?? null)}
              min={1}
              useGrouping={false}
              placeholder="מספר מדויק"
              inputStyle={styles.inputFull}
              style={styles.inputFull}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>טווח מספרי סידורי</label>
            <div style={styles.rangeRow}>
              <InputNumber
                value={filters.startSerialNumber}
                onValueChange={(e) =>
                  update('startSerialNumber', e.value ?? null)
                }
                min={1}
                useGrouping={false}
                placeholder="מ-"
                inputStyle={styles.inputFull}
                style={styles.inputFull}
              />
              <InputNumber
                value={filters.endSerialNumber}
                onValueChange={(e) =>
                  update('endSerialNumber', e.value ?? null)
                }
                min={1}
                useGrouping={false}
                placeholder="עד"
                inputStyle={styles.inputFull}
                style={styles.inputFull}
              />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>מתאריך</label>
            <Calendar
              value={filters.startFromDate}
              onChange={(e) =>
                update('startFromDate', (e.value as Date) ?? null)
              }
              dateFormat="dd/mm/yy"
              showIcon
              showButtonBar
              placeholder="מתאריך"
              style={{ width: '100%', direction: 'ltr' }}
              inputStyle={{ direction: 'rtl', width: '100%' }}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>עד תאריך</label>
            <Calendar
              value={filters.toDate}
              onChange={(e) => update('toDate', (e.value as Date) ?? null)}
              dateFormat="dd/mm/yy"
              showIcon
              showButtonBar
              placeholder="עד תאריך"
              style={{ width: '100%', direction: 'ltr' }}
              inputStyle={{ direction: 'rtl', width: '100%' }}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>סכום מדויק</label>
            <InputNumber
              value={filters.amount}
              onValueChange={(e) => update('amount', e.value ?? null)}
              mode="decimal"
              minFractionDigits={0}
              maxFractionDigits={2}
              placeholder="סכום"
              inputStyle={styles.inputFull}
              style={styles.inputFull}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>טווח סכומים</label>
            <div style={styles.rangeRow}>
              <InputNumber
                value={filters.amountFrom}
                onValueChange={(e) => update('amountFrom', e.value ?? null)}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                placeholder="מ-"
                inputStyle={styles.inputFull}
                style={styles.inputFull}
              />
              <InputNumber
                value={filters.amountTo}
                onValueChange={(e) => update('amountTo', e.value ?? null)}
                mode="decimal"
                minFractionDigits={0}
                maxFractionDigits={2}
                placeholder="עד"
                inputStyle={styles.inputFull}
                style={styles.inputFull}
              />
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <div style={styles.checkboxRow}>
            <div style={styles.checkboxItem}>
              <Checkbox
                inputId="showStudentDocument"
                checked={filters.showStudentDocument}
                onChange={(e) =>
                  update('showStudentDocument', e.checked ?? false)
                }
              />
              <label
                htmlFor="showStudentDocument"
                style={{ cursor: 'pointer' }}
              >
                הצג מסמכי סטודנטים
              </label>
            </div>
            <div style={styles.checkboxItem}>
              <Checkbox
                inputId="showCrmDocuments"
                checked={filters.showCrmDocuments}
                onChange={(e) => update('showCrmDocuments', e.checked ?? false)}
              />
              <label htmlFor="showCrmDocuments" style={{ cursor: 'pointer' }}>
                הצג מסמכי CRM
              </label>
            </div>
            <div style={styles.checkboxItem}>
              <Checkbox
                inputId="includeDocumentWithNoBranch"
                checked={filters.includeDocumentWithNoBranch}
                onChange={(e) =>
                  update('includeDocumentWithNoBranch', e.checked ?? false)
                }
              />
              <label
                htmlFor="includeDocumentWithNoBranch"
                style={{ cursor: 'pointer' }}
              >
                הצג מסמכים ללא שלוחה
              </label>
            </div>
          </div>

          <div style={styles.buttonsRow}>
            <Button
              icon="pi pi-filter-slash"
              label="נקה"
              text
              severity="secondary"
              onClick={handleClear}
              disabled={loading}
            />
            <Button
              icon="pi pi-search"
              label="חפש"
              onClick={onSearch}
              loading={loading}
            />
          </div>
        </div>

        <SelectPaymentServicesFilterDialog
          visible={showPaymentServicesDialog}
          onHide={() => setShowPaymentServicesDialog(false)}
          selectedIds={filters.paymentServiceIds}
          onConfirm={(selectedIds) => update('paymentServiceIds', selectedIds)}
        />
      </div>
    );
  });
